import { useState, useEffect } from "react";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Activity, Shield, Database, Users, Building2, ArrowRightLeft, TicketCheck, CheckCircle2, AlertTriangle, Clock, XCircle, Eye, Plus, Flag } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const KYC_STATUS_CONFIG = {
  Verified: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20 border-green-500/30" },
  "Under Review": { icon: Clock, color: "text-amber-400", bg: "bg-amber-500/20 border-amber-500/30" },
  Pending: { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/20 border-blue-500/30" },
  Expired: { icon: AlertTriangle, color: "text-orange-400", bg: "bg-orange-500/20 border-orange-500/30" },
  Flagged: { icon: XCircle, color: "text-red-400", bg: "bg-red-500/20 border-red-500/30" },
};

// Legacy records may carry lowercase `pending` while the dropdown uses the
// canonical PascalCase. Normalise so the Select always renders one of the
// valid options (defaulting to "Pending" when unknown).
const normalizeKycStatus = (raw) => {
  if (!raw) return "Pending";
  const lc = String(raw).trim().toLowerCase();
  const match = Object.keys(KYC_STATUS_CONFIG).find((k) => k.toLowerCase() === lc);
  return match || "Pending";
};

export default function StatusPage() {
  const navigate = useNavigate();
  const { fundId: portalFundId } = useParams();
  const [activeTab, setActiveTab] = useState("compliance");
  // When inside a fund portal, hide platform-wide system health tab.
  const showSystemTab = !portalFundId;
  useEffect(() => {
    if (!showSystemTab && activeTab === "system") setActiveTab("compliance");
  }, [showSystemTab, activeTab]);
  const [complianceData, setComplianceData] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEscalate, setShowEscalate] = useState(false);
  const [showResolve, setShowResolve] = useState(null);
  const [resolveNote, setResolveNote] = useState("");
  const [escalateForm, setEscalateForm] = useState({ investor_id: "", reason: "", priority: "medium" });
  const [submitting, setSubmitting] = useState(false);
  const [investors, setInvestors] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const compParams = portalFundId ? `?fund_id=${portalFundId}` : "";
      const escParams = portalFundId ? `?fund_id=${portalFundId}` : "";
      const requests = [
        API.get(`/status/compliance${compParams}`),
        API.get(`/compliance/escalations${escParams}`),
        API.get(portalFundId ? `/investors?fund_id=${portalFundId}` : "/investors"),
      ];
      // System health is platform-wide; only fetch it outside of fund-scoped portal.
      if (!portalFundId) requests.push(API.get("/status/system"));
      const results = await Promise.all(requests);
      setComplianceData(results[0].data);
      setEscalations(results[1].data);
      setInvestors(results[2].data);
      setSystemData(portalFundId ? null : results[3].data);
    } catch {
      toast.error("Failed to load status data");
    } finally {
      setLoading(false);
    }
  };

  const updateKYCStatus = async (investorId, newStatus) => {
    try {
      await API.put(`/investors/${investorId}/kyc-status`, { kyc_status: newStatus });
      toast.success("KYC status updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  const createEscalation = async () => {
    if (!escalateForm.investor_id || !escalateForm.reason) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/compliance/escalations", escalateForm);
      toast.success("Escalation created");
      setShowEscalate(false);
      setEscalateForm({ investor_id: "", reason: "", priority: "medium" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create escalation");
    } finally {
      setSubmitting(false);
    }
  };

  const resolveEscalation = async () => {
    if (!resolveNote) {
      toast.error("Please provide a resolution note");
      return;
    }
    setSubmitting(true);
    try {
      await API.put(`/compliance/escalations/${showResolve}?note=${encodeURIComponent(resolveNote)}`);
      toast.success("Escalation resolved");
      setShowResolve(null);
      setResolveNote("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resolve");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const openEscalations = escalations.filter((e) => e.status === "open");
  const resolvedEscalations = escalations.filter((e) => e.status === "resolved");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="status-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-3">
          <Activity className="w-7 h-7 text-primary" strokeWidth={1.5} />
          Internal Status
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Compliance, escalations, and system health</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary">
          <TabsTrigger value="compliance" className="gap-2" data-testid="tab-compliance">
            <Shield className="w-4 h-4" /> Compliance
          </TabsTrigger>
          <TabsTrigger value="escalations" className="gap-2" data-testid="tab-escalations">
            <Flag className="w-4 h-4" /> Escalations
            {openEscalations.length > 0 && (
              <Badge variant="destructive" className="ml-1 text-[10px] h-5 px-1.5">{openEscalations.length}</Badge>
            )}
          </TabsTrigger>
          {showSystemTab && (
            <TabsTrigger value="system" className="gap-2" data-testid="tab-system">
              <Database className="w-4 h-4" /> System
            </TabsTrigger>
          )}
        </TabsList>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(KYC_STATUS_CONFIG).map(([status, config]) => {
              const Icon = config.icon;
              const count = complianceData?.summary?.[status] || 0;
              return (
                <Card key={status} className={`bg-card border ${config.bg}`} data-testid={`kyc-card-${status.toLowerCase().replace(" ", "-")}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-sm text-muted-foreground">{status}</p>
                      </div>
                      <Icon className={`w-8 h-8 ${config.color}`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Investors Requiring Attention
                <Badge variant="secondary" className="ml-2">{complianceData?.attention_count || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {complianceData?.attention_needed?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p>All investors are verified.</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead>Fund</TableHead>
                        <TableHead>KYC Status</TableHead>
                        <TableHead>Onboarding</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {complianceData?.attention_needed?.map((inv) => {
                        const currentStatus = normalizeKycStatus(inv.kyc_status);
                        const kycConfig = KYC_STATUS_CONFIG[currentStatus];
                        return (
                          <TableRow key={inv.id} data-testid={`compliance-row-${inv.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{inv.name}</p>
                                <p className="text-xs text-muted-foreground">{inv.email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{inv.fund_name}</TableCell>
                            <TableCell>
                              <Select value={currentStatus} onValueChange={(v) => updateKYCStatus(inv.id, v)}>
                                <SelectTrigger className={`w-[140px] h-8 ${kycConfig.bg} border`} data-testid={`kyc-select-${inv.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.keys(KYC_STATUS_CONFIG).map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs capitalize">{inv.onboarding_status}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => navigate(`/super-admin/investors/${inv.id}`)} data-testid={`view-investor-${inv.id}`}>
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>

          {/* All Investors — full list with inline KYC status updates. This is
              the canonical "Investors with status" section the SA uses to
              triage every investor on the fund, not just the ones flagged as
              needing attention. */}
          <Card className="bg-card border-border" data-testid="all-investors-status-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Investors — KYC Status
                <Badge variant="secondary" className="ml-2">{investors.length}</Badge>
              </CardTitle>
              <CardDescription>Update KYC status inline. Changes apply immediately.</CardDescription>
            </CardHeader>
            <CardContent>
              {investors.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No investors yet.
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Investor</TableHead>
                        <TableHead>Fund</TableHead>
                        <TableHead>KYC Status</TableHead>
                        <TableHead>Onboarding</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {investors.map((inv) => {
                        const currentStatus = normalizeKycStatus(inv.kyc_status);
                        const kycConfig = KYC_STATUS_CONFIG[currentStatus];
                        return (
                          <TableRow key={inv.id} data-testid={`all-investors-row-${inv.id}`}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{inv.name}</p>
                                <p className="text-xs text-muted-foreground">{inv.email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{inv.fund_name || "—"}</TableCell>
                            <TableCell>
                              <Select value={currentStatus} onValueChange={(v) => updateKYCStatus(inv.id, v)}>
                                <SelectTrigger className={`w-[140px] h-8 ${kycConfig.bg} border`} data-testid={`all-kyc-select-${inv.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.keys(KYC_STATUS_CONFIG).map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs capitalize">{inv.onboarding_status || "pending"}</Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(portalFundId
                                  ? `/super-admin/funds/${portalFundId}/portal/investors/${inv.id}`
                                  : `/super-admin/investors/${inv.id}`)}
                                data-testid={`view-all-investor-${inv.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Escalations Tab */}
        <TabsContent value="escalations" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="bg-red-500/15 text-red-400 border-red-500/30">{openEscalations.length} Open</Badge>
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30">{resolvedEscalations.length} Resolved</Badge>
            </div>
            <Button size="sm" onClick={() => setShowEscalate(true)} className="gap-1.5" data-testid="create-escalation-btn">
              <Plus className="w-4 h-4" /> New Escalation
            </Button>
          </div>

          {/* Open Escalations */}
          <Card data-testid="open-escalations-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-heading">Open Escalations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {openEscalations.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground text-sm">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  No open escalations
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Investor</TableHead>
                      <TableHead>Fund</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right pr-6">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openEscalations.map((esc) => (
                      <TableRow key={esc.id} data-testid={`escalation-row-${esc.id}`}>
                        <TableCell className="pl-6">
                          <p className="font-medium text-sm">{esc.investor_name}</p>
                          <p className="text-xs text-muted-foreground">{esc.investor_email}</p>
                        </TableCell>
                        <TableCell className="text-sm">{esc.fund_name}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{esc.reason}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            esc.priority === "high" ? "bg-red-500/15 text-red-400 border-red-500/30 text-[10px]" :
                            esc.priority === "medium" ? "bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]" :
                            "bg-zinc-500/15 text-zinc-400 border-zinc-500/30 text-[10px]"
                          }>
                            {esc.priority}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{esc.created_by_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(esc.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</TableCell>
                        <TableCell className="text-right pr-6">
                          <Button size="sm" variant="outline" onClick={() => { setShowResolve(esc.id); setResolveNote(""); }} data-testid={`resolve-${esc.id}`} className="text-xs gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Resolved Escalations */}
          {resolvedEscalations.length > 0 && (
            <Card data-testid="resolved-escalations-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-heading text-muted-foreground">Resolved Escalations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">Investor</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Resolution</TableHead>
                      <TableHead>Resolved By</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resolvedEscalations.map((esc) => (
                      <TableRow key={esc.id} className="opacity-70">
                        <TableCell className="pl-6 text-sm">{esc.investor_name}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{esc.reason}</TableCell>
                        <TableCell className="text-sm max-w-[200px] truncate">{esc.resolution_note}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{esc.resolved_by}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{esc.resolved_at ? new Date(esc.resolved_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* System Tab */}
        <TabsContent value="system" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Active Funds", value: `${systemData?.metrics?.active_funds || 0}/${systemData?.metrics?.total_funds || 0}`, icon: Building2, color: "text-primary" },
              { label: "Total Investors", value: systemData?.metrics?.total_investors || 0, icon: Users, color: "text-blue-400" },
              { label: "Total Transactions", value: (systemData?.metrics?.total_capital_transactions || 0) + (systemData?.metrics?.total_performance_transactions || 0), icon: ArrowRightLeft, color: "text-green-400" },
              { label: "Open Tickets", value: systemData?.metrics?.open_support_tickets || 0, icon: TicketCheck, color: "text-amber-400" },
            ].map((m) => (
              <Card key={m.label} className="bg-card border-border" data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl font-bold">{m.value}</p>
                      <p className="text-sm text-muted-foreground">{m.label}</p>
                    </div>
                    <m.icon className={`w-8 h-8 ${m.color}`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="bg-card border-border" data-testid="verification-rate-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Investor Verification Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Progress value={systemData?.metrics?.investor_verification_rate || 0} className="flex-1 h-3" />
                <span className="text-xl font-bold min-w-[60px]">{systemData?.metrics?.investor_verification_rate || 0}%</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {systemData?.metrics?.verified_investors || 0} of {systemData?.metrics?.total_investors || 0} investors verified
              </p>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="w-5 h-5 text-primary" /> Fund Setup Completion
              </CardTitle>
            </CardHeader>
            <CardContent>
              {systemData?.fund_completion?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No funds configured yet.</div>
              ) : (
                <ScrollArea className="h-[350px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fund</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Completion</TableHead>
                        <TableHead>Investors</TableHead>
                        <TableHead>Transactions</TableHead>
                        <TableHead>Checks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {systemData?.fund_completion?.map((fund) => (
                        <TableRow key={fund.id} data-testid={`system-fund-row-${fund.id}`}>
                          <TableCell className="font-medium">{fund.name}</TableCell>
                          <TableCell>
                            <Badge variant={fund.status === "active" ? "default" : "secondary"} className="capitalize">{fund.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <Progress value={fund.completion_percent} className="flex-1 h-2" />
                              <span className="text-sm font-medium">{fund.completion_percent}%</span>
                            </div>
                          </TableCell>
                          <TableCell>{fund.investor_count}</TableCell>
                          <TableCell>{fund.transaction_count}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {Object.entries(fund.checks || {}).map(([key, val]) => (
                                <span key={key} className={`w-2 h-2 rounded-full ${val ? "bg-green-400" : "bg-zinc-600"}`} title={`${key.replace(/_/g, " ")}: ${val ? "Yes" : "No"}`} />
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Escalation Dialog */}
      <Dialog open={showEscalate} onOpenChange={setShowEscalate}>
        <DialogContent className="sm:max-w-lg" data-testid="escalation-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Compliance Escalation</DialogTitle>
            <DialogDescription>Flag an investor for compliance review</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Investor *</Label>
              <Select value={escalateForm.investor_id} onValueChange={(v) => setEscalateForm((p) => ({ ...p, investor_id: v }))}>
                <SelectTrigger data-testid="escalation-investor-select"><SelectValue placeholder="Select investor" /></SelectTrigger>
                <SelectContent>
                  {investors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={escalateForm.priority} onValueChange={(v) => setEscalateForm((p) => ({ ...p, priority: v }))}>
                <SelectTrigger data-testid="escalation-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Reason *</Label>
              <Textarea value={escalateForm.reason} onChange={(e) => setEscalateForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Describe the compliance concern..." rows={3} data-testid="escalation-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalate(false)}>Cancel</Button>
            <Button onClick={createEscalation} disabled={submitting} data-testid="escalation-submit-btn">
              {submitting ? "Creating..." : "Create Escalation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Escalation Dialog */}
      <Dialog open={!!showResolve} onOpenChange={(open) => { if (!open) setShowResolve(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Resolve Escalation</DialogTitle>
            <DialogDescription>Provide a resolution note</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Resolution details..." rows={3} data-testid="resolve-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolve(null)}>Cancel</Button>
            <Button onClick={resolveEscalation} disabled={submitting} data-testid="resolve-submit-btn">
              {submitting ? "Resolving..." : "Resolve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
